import React from "react";
import { useSelector } from "react-redux";

import {
  itineraryToTripSegment,
  itineraryToTripSlice,
} from "@b2bportal/core-flights";
import { FlightsRebookSelectors } from "@b2bportal/core-flights-rebook";
import { SliceDirection } from "@b2bportal/core-types";
import type { RebookFlightSegmentProps } from "./types";

export const useRebookFlightSegment = ({
  slice: itinerarySlice,
  segment: itinerarySegment,
  direction,
  segmentId,
  route,
  airlines,
  airports,
}: RebookFlightSegmentProps) => {
  const lastSegment = React.useMemo(
    () => itinerarySlice.segments[itinerarySlice.segments.length - 1],
    [itinerarySlice]
  );
  const destinationCode = lastSegment.destination.locationCode;
  const destinationName =
    airports[destinationCode]?.cityName ?? destinationCode;

  const selectedSegment = useSelector(
    FlightsRebookSelectors.getSelectedSegment
  );

  const isSegmentSelected = React.useCallback(
    (id: string) => {
      if (selectedSegment == null) {
        return false;
      }
      const { selection } = selectedSegment;
      const selectedId = `${selection.slice}/${selection.segment}`;
      return id === selectedId;
    },
    [selectedSegment]
  );

  const disabled = route == null;
  const selected = isSegmentSelected(segmentId);

  const slice = React.useMemo(
    () =>
      itineraryToTripSlice(
        itinerarySlice,
        direction === SliceDirection.Outbound,
        airlines,
        airports
      ),
    [airlines, airports, direction, itinerarySlice]
  );

  const segment = React.useMemo(
    () => itineraryToTripSegment(itinerarySegment, airlines, airports),
    [airlines, airports, itinerarySegment]
  );

  return React.useMemo(
    () => ({
      text: {
        destinationName,
        destinationCode,
      },
      context: {
        airports,
        airlines,
        slice,
        segment,
        segmentId,
        disabled,
        selected,
      },
    }),
    [
      airlines,
      airports,
      destinationCode,
      destinationName,
      disabled,
      segment,
      segmentId,
      selected,
      slice,
    ]
  );
};
